import { $api, fetchClient } from '@/api/typedClient'
import type { components } from '@/types/api/v1'
import type { IServerSideGetRowsRequest } from 'ag-grid-community'

export type SPRow = components['schemas']['SPRow']
export type SPRowComponent = components['schemas']['SPRowComponent']
export type SupplyPlanHistoryItem = components['schemas']['SupplyPlanHistory']

// Business filters for the supply plan grid
export interface SupplyPlanFilters {
  asOf: string
  /** Prod/Test. Undefined is "All" -- see toModeParam in @/utils/envMode. */
  mode?: components['schemas']['EnvMode']
  /** Not sent to the server -- keys the datasource cache so a tenant switch refetches. */
  tenantName?: string
}

/**
 * SSRM fetch function (a plain async function, not a hook) for
 * {@link useServerSideDataSource}, mapped onto `POST /crossroads/supply_plan/find`.
 * Column definitions are hardcoded on the frontend (like the Orders page), not
 * backend-driven.
 */
export async function fetchSupplyPlans({
  filters,
  search,
  request,
}: {
  filters: SupplyPlanFilters
  search: string
  request: IServerSideGetRowsRequest & { includeCount?: boolean }
}): Promise<{ rowData: SPRow[]; rowCount: number }> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/supply_plan/find',
    {
      body: {
        ...request,
        search,
        mode: filters.mode,
        startRow: request.startRow ?? 0,
        endRow: request.endRow ?? 100,
        includeCount: request.includeCount ?? true,
        rowGroupCols: (request.rowGroupCols ?? []).flatMap((col) =>
          col.field
            ? [{ id: col.id, displayName: col.displayName, field: col.field }]
            : [],
        ),
        // batch_id (deep-linked from the Integration Logs page) is a real
        // set-filter on the Batch column now, applied once via the grid API
        // (see SupplyPlansPage.tsx's onFirstDataRendered) instead of being
        // force-merged in here -- that used to fight the user's own
        // checkbox choices in the Batch filter dropdown once they touched it.
        filterModel: {
          ...request.filterModel,
          as_of: { value: filters.asOf },
        },
      },
    },
  )
  if (error) throw error
  return {
    rowData: (data.rowData ?? []) as SPRow[],
    rowCount: data.rowCount,
  }
}

/**
 * Bulk-downloads every row matching the grid's current filters (not just the
 * current page) as an Excel workbook -- same shape as
 * `/crossroads/mappings/bulk_download` (see UploadDownloadButtons.tsx).
 */
export async function fetchSupplyPlanBulkDownload({
  filters,
  filterModel,
}: {
  filters: SupplyPlanFilters
  filterModel?: IServerSideGetRowsRequest['filterModel']
}): Promise<Blob> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/supply_plan/bulk_download',
    {
      body: {
        startRow: 0,
        endRow: 0,
        includeCount: false,
        mode: filters.mode,
        filterModel: {
          ...filterModel,
          as_of: { value: filters.asOf },
        },
      },
      parseAs: 'blob',
    },
  )
  if (error) throw error
  return data as unknown as Blob
}

export async function fetchSupplyPlanFilterValues(
  column: string,
  filters: SupplyPlanFilters,
): Promise<(string | null)[]> {
  const { data, error } = await fetchClient.POST(
    '/crossroads/supply_plan/filter_values',
    {
      params: { query: { column } },
      body: {
        startRow: 0,
        endRow: 0,
        includeCount: false,
        mode: filters.mode,
        filterModel: { as_of: { value: filters.asOf } },
      },
    },
  )
  if (error) throw error
  return data
}

/**
 * Fetches the translation audit recorded on a single supply plan -- the mapping
 * engine's per-field reasoning for the origin -> master -> destination legs.
 *
 * The order equivalent (`useAudit`) looks its audit up by saga id from a
 * collection of its own; a supply plan's is embedded on the plan itself, so this
 * is keyed by supply plan id instead.
 */
export function useSupplyPlanAudit(supplyPlanId: string | null) {
  return $api.useQuery(
    'post',
    '/crossroads/supply_plan/audit',
    {
      params: { query: { supply_plan_id: supplyPlanId! } },
    },
    { enabled: !!supplyPlanId },
  )
}
